/**
 * Footer markup — the storefront's copy of the designer's `footerMarkup.js`,
 * kept identical so preview and published page cannot drift.
 *
 * The published `html_content` carries no footer: `buildHtmlExport()` strips
 * `<footer>…</footer>` so the canvas re-renders it from the store's settings
 * rather than serialising a stale copy. The storefront builds it from
 * `page_data` for the same reason.
 */

import { escapeHtml, phoneDisplay } from '../../utilities/helper';
import { readableOn } from '../../shared/theme/storefrontTheme';

/** "1420 SE Division St" / "Portland, OR 97201" — the second line only appears
 *  once there is something to put on it. */
export const formatAddressLines = ({ street, city, state, zip } = {}) => {
  const line1 = String(street || '').trim();
  const locality = [String(city || '').trim(), String(state || '').trim()]
    .filter(Boolean)
    .join(', ');
  const line2 = [locality, String(zip || '').trim()].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean);
};

/** Stored links are scheme-less (Store profile strips "https://" for display
 *  before saving) — used bare as an href, that resolves as a path on the
 *  current storefront instead of leaving the site. */
const ensureScheme = (url) => {
  const clean = String(url || '').trim();
  if (!clean) return '';
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(clean) ? clean : `https://${clean}`;
};

/** "https://test.com/shop" reads as "test.com" — the footer shows where the
 *  link goes, not the whole URL. */
export const displayHost = (url) => {
  const clean = String(url || '').trim();
  if (!clean) return '';
  try {
    return new URL(clean.includes('://') ? clean : `https://${clean}`).hostname.replace(/^www\./, '');
  } catch {
    return clean.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
  }
};

const SOCIAL_ICONS = {
  website:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18"/></svg>',
  facebook:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 9h3V6h-3c-2.2 0-4 1.8-4 4v2H8v3h2v6h3v-6h3l1-3h-4v-2c0-.6.4-1 1-1z"/></svg>',
  instagram:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><rect x="3.5" y="3.5" width="17" height="17" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17" cy="7" r="1.1" fill="currentColor" stroke="none"/></svg>',
  tiktok:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 3h2.6a5.4 5.4 0 0 0 4.4 4.3V10a8 8 0 0 1-4.4-1.5v6.2A5.7 5.7 0 1 1 11 9v2.8a2.9 2.9 0 1 0 2.1 2.8V3z"/></svg>',
  youtube:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15V9l5.2 3z"/></svg>',
  animalList:
    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="6.5" cy="10" r="2.1"/><circle cx="12" cy="7.8" r="2.1"/><circle cx="17.5" cy="10" r="2.1"/><path d="M12 12.4c-3 0-5 2.2-5 4.4C7 18.6 9 19.2 12 19.2s5-.6 5-2.4c0-2.2-2-4.4-5-4.4z"/></svg>',
};

const SOCIAL_LABELS = {
  website: 'Website',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  animalList: 'The Animal List',
};

/* Order is fixed rather than driven by whatever order the values object happens
   to have, so the row does not reshuffle as a partner fills links in. */
const SOCIAL_ORDER = ['website', 'facebook', 'instagram', 'tiktok', 'youtube', 'animalList'];

const socialsHtml = (links = {}) => {
  const set = SOCIAL_ORDER.filter((key) => String(links[key] || '').trim());
  if (!set.length) return '';

  const cells = set
    .map(
      (key) =>
        `<a class="foot-s" href="${escapeHtml(ensureScheme(links[key]))}" target="_blank" rel="noopener noreferrer" aria-label="${SOCIAL_LABELS[key]}" title="${SOCIAL_LABELS[key]}">${SOCIAL_ICONS[key]}</a>`
    )
    .join('');

  return `<div class="foot-social">${cells}</div>`;
};

/** Contact only appears when the store has given something to contact it by. */
const contactHtml = ({ address, phone, email, website }) => {
  const lines = formatAddressLines(address);
  const tel = String(phone || '').trim();
  const mail = String(email || '').trim();
  const site = String(website || '').trim();
  if (!lines.length && !tel && !mail && !site) return '';

  /* Each address line is its own row, on the same 10px rhythm as the phone,
     email and the Shop column — a <br/> block ran on a tighter rhythm than the
     rest of the column (reference build, 2026-10-03). */
  const addressBlock = lines.map((l) => `<span class="foot-line">${escapeHtml(l)}</span>`).join('');
  /* Shown formatted: raw digits were reaching customers as typed. */
  const telLink = tel
    ? `<a href="tel:${escapeHtml(tel.replace(/[^0-9+]/g, ''))}">${escapeHtml(phoneDisplay(tel))}</a>`
    : '';
  const mailLink = mail ? `<a href="mailto:${escapeHtml(mail)}">${escapeHtml(mail)}</a>` : '';
  const siteLink = site
    ? `<a href="${escapeHtml(ensureScheme(site))}" target="_blank" rel="noopener noreferrer">${escapeHtml(displayHost(site))}</a>`
    : '';

  return `<div class="foot-col"><h4>Contact</h4>${addressBlock}${telLink}${mailLink}${siteLink}</div>`;
};

/**
 * Build the storefront footer.
 *
 * @param {object} o
 * @param {string} o.storeName
 * @param {string} o.logoUrl      the store's logo, if one is uploaded
 * @param {string} o.initials     fallback mark when there is no logo
 * @param {string} o.message      the one line under the store name
 * @param {object} o.address      { street, city, state, zip }
 * @param {string} o.phone
 * @param {string} o.email
 * @param {string} o.website      shown in Contact when the store opts in
 * @param {boolean} o.showSocials whether the social row is drawn at all
 * @param {Array}  o.shopLinks    [{ label, link }] for the Shop column
 * @param {object} o.socials      { facebook, instagram, ... }
 * @param {string} o.themeColor   the Style tab's Storefront color
 * @param {string} o.headingFont  the Style tab's heading font
 * @param {string} o.disclosure   the store-wide commission/seller disclosure
 * @param {Array}  o.legalLinks   [{ label, href }] for the bottom row
 */
export const buildFooterHtml = ({
  storeName = '',
  logoUrl = '',
  initials = '',
  message = '',
  address = {},
  phone = '',
  email = '',
  website = '',
  showSocials = false,
  shopLinks = [],
  socials = {},
  themeColor = '#E15439',
  headingFont = '',
  disclosure = '',
  legalLinks = [],
} = {}) => {
  const name = escapeHtml(storeName);

  const mark = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${name}" />`
    : `<span class="foot-initials" style="color:${readableOn(themeColor)}">${escapeHtml(initials)}</span>`;

  const messageLine = String(message || '').trim()
    ? `<p class="foot-msg">${escapeHtml(message)}</p>`
    : '';

  /* Store-wide commission/seller disclosure — the browsing-path copy the
     compliance mapping requires on every page. Plain language, no "merchant of
     record". */
  const disclosureLine = String(disclosure || '').trim()
    ? `<p class="foot-disclosure">${escapeHtml(disclosure)}</p>`
    : '';

  const shop = shopLinks.length
    ? `<div class="foot-col foot-shop"><h4>Shop</h4>${shopLinks
        .map((l) => `<a href="${escapeHtml(l.link || '#')}">${escapeHtml(l.label || '')}</a>`)
        .join('')}</div>`
    : '';

  const year = new Date().getFullYear();

  /* The designer's footer has no legal column — it only draws the home page —
     but these links have to be reachable from every page, so they ride in the
     bottom row opposite the copyright. */
  const legal = legalLinks.length
    ? `<span class="foot-legal">${legalLinks
        .map(
          (l) =>
            `<a href="${escapeHtml(l.href || '#')}" target="_blank" rel="noopener noreferrer">${escapeHtml(l.label || '')}</a>`
        )
        .join('')}</span>`
    : '';

  return `<footer class="sf-foot">
  <div class="sf-foot-in">
    <div class="foot-top">
      <div class="foot-brand">
        <div class="fb-row">
          <span class="foot-logo"${logoUrl ? '' : ` style="background:${escapeHtml(themeColor)}"`}>${mark}</span>
          <span class="foot-name"${headingFont ? ` style="font-family:${headingFont}"` : ''}>${name}</span>
        </div>
        ${messageLine}
        ${showSocials ? socialsHtml(socials) : ''}
        <div class="foot-power">Powered by <b>ShopDot</b></div>
      </div>
      ${contactHtml({ address, phone, email, website })}
      ${shop}
    </div>
    ${disclosureLine}
    <div class="foot-bottom">
      <span>© ${year} ${name}. All rights reserved.</span>
      ${legal}
    </div>
  </div>
</footer>`;
};

/* The designer's Shop column: the storefront's own destinations, the same four
   the reference footer carries. Deliberately not the header's navigation, which
   the partner can reorder and hide from — a footer that changed shape with it
   would stop being a reliable index.

   The two product hrefs differ from the designer's `/products`, which is not a
   route this app serves. The labels are the designer's. */
export const FOOTER_SHOP_LINKS = [
  { label: 'All products', link: '/all-products' },
  { label: 'Collections', link: '/all-collections' },
  { label: 'Brands', link: '/all-brands' },
  { label: 'New arrivals', link: '/all-products?sort=newest' },
];

/** "Riverside Pet Co." → "RP", the mark shown when no logo is uploaded. */
export const initialsOf = (storeName) =>
  String(storeName || '')
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'RP';

export default buildFooterHtml;
